import { useEffect } from 'react';
import { DuoColorIcon, Icon } from '@zillow/constellation-10';
import { IconShieldCheckmarkFilled } from '@zillow/constellation-icons';
import { useState } from 'react';
import EmptyCard from './EmptyCard';
import type { YourRentalRinsCardDto } from '@zg-rentals/ts-your-rental-api-client';

const UPSELL_DISMISSED_STORAGE_KEY = 'hasRentersInsuranceUpsellBeenDismissedForListingAlias-';

type RentersInsuranceCardProps = {
  rentersInsuranceCard?: YourRentalRinsCardDto;
  listingAlias?: string;
};

const RentersInsuranceEmptyCard = ({
  rinsCard,
  listingAlias,
  onDismiss,
}: {
  rinsCard: YourRentalRinsCardDto;
  listingAlias?: string;
  onDismiss: () => void;
}) => {
  const handleClose = () => {
    if (listingAlias) {
      localStorage.setItem(`${UPSELL_DISMISSED_STORAGE_KEY}${listingAlias}`, JSON.stringify(true));
    }
    onDismiss();
  };

  return (
    <EmptyCard
      icon={
        <DuoColorIcon tone="inspire">
          <Icon>
            <IconShieldCheckmarkFilled />
          </Icon>
        </DuoColorIcon>
      }
      title={rinsCard.cardTitle}
      description={rinsCard.cardText}
      link={rinsCard.cta.url}
      buttonText={rinsCard.cta.text}
      onClose={listingAlias ? handleClose : undefined}
      buttonAriaLabel={rinsCard.cta.action}
      dataTestId="RentersInsurance"
    />
  );
};

const hasUpsellBeenDismissed = (listingAlias?: string): boolean => {
  if (!listingAlias) {
    return false;
  }

  const localStorageValue = localStorage.getItem(`${UPSELL_DISMISSED_STORAGE_KEY}${listingAlias}`);
  if (localStorageValue !== null) {
    return JSON.parse(localStorageValue);
  }

  return false;
};

const RentersInsuranceCard = ({ rentersInsuranceCard, listingAlias }: RentersInsuranceCardProps) => {
  const [isUpsellDismissed, setIsUpsellDismissed] = useState(() => {
    return hasUpsellBeenDismissed(listingAlias);
  });

  useEffect(() => {
    setIsUpsellDismissed(hasUpsellBeenDismissed(listingAlias));
  }, [listingAlias]);

  const handleDismiss = () => {
    setIsUpsellDismissed(true);
  };

  if (rentersInsuranceCard && !isUpsellDismissed) {
    return (
      <RentersInsuranceEmptyCard
        rinsCard={rentersInsuranceCard}
        listingAlias={listingAlias}
        onDismiss={handleDismiss}
      />
    );
  }

  return null;
};

export default RentersInsuranceCard;
